import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosFilterBar, IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const STATUS_FILTERS = [
  { key: "all", label: "All", icon: "filter_list" },
  { key: "completed", label: "Completed", icon: "check_circle", accent: "green" },
  { key: "in_progress", label: "In progress", icon: "pending", accent: "orange" },
  { key: "not_started", label: "Not started", icon: "radio_button_unchecked" },
  { key: "warranty", label: "Warranty", icon: "verified_user", accent: "red" },
  { key: "sold", label: "Sold", icon: "local_shipping", accent: "green" },
];

const computeStageStatus = (stageStatus) => {
  const s = String(stageStatus || "").toLowerCase();
  if (s === "sold") return { key: "sold", label: "Sold", color: "success" };
  if (s === "warranty") return { key: "warranty", label: "Warranty", color: "warning" };
  if (s === "not_started") return { key: "not_started", label: "Not started", color: "default" };
  if (s === "passed" || s === "complete" || s === "completed") return { key: "completed", label: "Completed", color: "success" };
  if (s === "in_progress") return { key: "in_progress", label: "In progress", color: "info" };
  if (s === "blocked" || s === "failed") return { key: "in_progress", label: "In progress", color: "info" };
  if (s === "pending") return { key: "not_started", label: "Not started", color: "default" };
  return { key: "not_started", label: "Not started", color: "default" };
};

export default function ValveBodiesMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["m-valve-bodies", search, page, pageSize],
    queryFn: () =>
      get("/api/valve-bodies", {
        sort: "updatedAt:desc",
        populate: { workOrder: true, finishedGood: true },
        pagination: { page, pageSize },
        filters: {
          ...(search && {
            $or: [
              { serialNumber: { $containsi: search } },
              { optionalTag: { $containsi: search } },
              { finishedGood: { sku: { $containsi: search } } },
            ],
          }),
        },
      }),
    staleTime: 5_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  const unitIds = useMemo(() => rows.map((r) => String(r.documentId || r.id)).filter(Boolean), [rows]);

  // Latest stage record per unit (for current page)
  const { data: stageRecordsData } = useQuery({
    queryKey: ["m-valve-bodies-stage-records", unitIds.join("|")],
    queryFn: async () => {
      if (!unitIds.length) return { data: [] };
      const cleanIds = Array.from(new Set(unitIds.map((id) => String(id || "").trim()).filter(Boolean)));
      const numericIds = cleanIds.map((id) => Number(id)).filter((n) => Number.isFinite(n));
      const docIds = cleanIds.filter((id) => !Number.isFinite(Number(id)));
      const orFilters = [];
      if (numericIds.length) orFilters.push({ unit: { id: { $in: numericIds } } });
      if (docIds.length) orFilters.push({ unit: { documentId: { $in: docIds } } });
      if (!orFilters.length) return { data: [] };
      return (
        (await get("/api/stage-records", {
          populate: { unit: true },
          sort: "createdAt:desc",
          filters: { $or: orFilters },
          pagination: { pageSize: 500 },
        })) || { data: [] }
      );
    },
    enabled: unitIds.length > 0,
    staleTime: 5_000,
  });

  const stageStatusMap = useMemo(() => {
    const map = new Map();
    (stageRecordsData?.data || []).forEach((rec) => {
      const r = rec?.attributes ? { id: rec.id, documentId: rec.documentId || rec.id, ...rec.attributes } : rec;
      const unit = r?.unit?.data || r?.unit;
      const u = unit?.attributes ? { id: unit.id, documentId: unit.documentId || unit.id, ...unit.attributes } : unit;
      const key = String(u?.documentId || u?.id || "");
      if (!key) return;
      if (!map.has(key)) map.set(key, r);
    });
    return map;
  }, [stageRecordsData]);

  // Warranty claims (recent) — mark unit warranty when latest claim status not closed/denied
  const { data: warrantyClaimsData } = useQuery({
    queryKey: ["m-warranty-claims-recent"],
    queryFn: () =>
      get("/api/warranty-claims", {
        populate: { unit: true },
        sort: "createdAt:desc",
        pagination: { pageSize: 500 },
      }),
    staleTime: 10_000,
  });

  const warrantyMap = useMemo(() => {
    const unitSet = new Set(unitIds);
    const byUnit = new Map();
    (warrantyClaimsData?.data || []).forEach((c) => {
      const claim = c?.attributes ? { id: c.id, documentId: c.documentId || c.id, ...c.attributes } : c;
      const unit = claim?.unit?.data || claim?.unit;
      const u = unit?.attributes ? { id: unit.id, documentId: unit.documentId || unit.id, ...unit.attributes } : unit;
      const key = String(u?.documentId || u?.id || "");
      if (!key || !unitSet.has(key)) return;
      if (!byUnit.has(key)) byUnit.set(key, claim);
    });
    const m = new Map();
    byUnit.forEach((claim, unitId) => {
      const st = String(claim?.status || "").toLowerCase();
      if (st !== "closed" && st !== "denied") m.set(unitId, true);
    });
    return m;
  }, [warrantyClaimsData, unitIds]);

  const filteredRows = useMemo(() => {
    if (status === "all") return rows;
    return rows.filter((r) => {
      const key = String(r.documentId || r.id);
      const sr = stageStatusMap.get(key);
      const isWarranty = warrantyMap.has(key);
      const backendStatus = String(r.progressStatus || "").toLowerCase();
      const statusChip = computeStageStatus(
        backendStatus || (isWarranty ? "warranty" : sr?.status)
      );
      return statusChip.key === status;
    });
  }, [rows, status, stageStatusMap, warrantyMap]);

  return (
    <IosPage>
      <MobileTopBar
        title="Valve Bodies"
        subtitle={typeof total === "number" ? `${total} total • page ${page}/${pageCount}` : "Tags"}
        onBack={() => navigate("/m/dashboard")}
        right={
          <IconButton
            aria-label="Refresh"
            size="small"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["m-valve-bodies"] });
              qc.invalidateQueries({ queryKey: ["m-valve-bodies-stage-records"] });
              qc.invalidateQueries({ queryKey: ["m-warranty-claims-recent"] });
            }}
          >
            <Icon fontSize="small">refresh</Icon>
          </IconButton>
        }
      />

      {(isLoading || isFetching) && <LinearProgress sx={{ mt: 1 }} />}

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tag / SKU / WO…"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton aria-label="Clear" size="small" onClick={() => setSearch("")}>
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <IosFilterBar label="Lifecycle" value={status} options={STATUS_FILTERS} onChange={setStatus} />

        <IosList>
          {filteredRows.map((u) => {
            const documentId = u.documentId;
            const rowKey = documentId || u.id;
            const tag = u.optionalTag || u.serialNumber || "";
            const sku = u.finishedGood?.sku || "";
            const woNum = u.workOrder?.workOrderNumber || "—";
            const sr = stageStatusMap.get(String(rowKey));
            const backendStatus = String(u.progressStatus || "").toLowerCase();
            const isWarranty = warrantyMap.has(String(rowKey));
            const lifecycleChip = computeStageStatus(backendStatus || (isWarranty ? "warranty" : sr?.status));

            return (
              <IosListItem
                key={String(rowKey)}
                icon="qr_code_2"
                title={sku || "Part number pending"}
                meta={(tag ? `Tag ${tag}` : "Tag pending") + ` • WO ${woNum}`}
                trailing={lifecycleChip.label}
                accent={lifecycleChip.key === "completed" || lifecycleChip.key === "sold" ? "green" : lifecycleChip.key === "warranty" ? "red" : "orange"}
                onClick={() => {
                  if (documentId) navigate(`/m/internal/valve-bodies/${documentId}`);
                }}
              />
            );
          })}
          {!isLoading && filteredRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No valve bodies found.
            </Typography>
          ) : null}
        </IosList>

        {pageCount > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
            <Pagination count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
          </Box>
        ) : null}
      </Stack>
    </IosPage>
  );
}
